import { createPopper, Instance, Modifier, Placement } from '@popperjs/core';
import { isNull, isUndefined } from 'lodash-es';
import React, { useLayoutEffect } from 'react';

export interface IUsePopperProps {
  gutter?: number;
  placement?: Placement;
  offset?: [number, number];
  preventOverflow?: boolean;
  fixed?: boolean;
  forceUpdate?: boolean;
  flip?: boolean;
  eventsEnabled?: boolean;
  modifiers?: Modifier<any, any>[];
}

export const getFixedAttr = (fixed, width): boolean => {
  if (isUndefined(fixed)) {
    return width === 'full' ? false : true;
  }
  if (!isNull(fixed)) {
    return fixed;
  }
};

export function usePopper(props: IUsePopperProps) {
  const {
    placement: initialPlacement = 'bottom',
    offset: offsetProp,
    preventOverflow = true,
    fixed = false,
    forceUpdate = true,
    flip = true,
    eventsEnabled = true,
    modifiers = []
  } = props;

  const popper = React.useRef<Instance | null>(null);
  const referenceRef = React.useRef<HTMLButtonElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  const [originalPlacement, place] = React.useState(initialPlacement);
  const [placement, setPlacement] = React.useState(initialPlacement);
  const [offset] = React.useState(offsetProp || [0, 0]);
  const [popoverStyles, setPopoverStyles] = React.useState<React.CSSProperties>({});

  const update = React.useCallback(() => {
    if (popper.current) {
      popper.current.forceUpdate();
      return true;
    }
    return false;
  }, []);

  useLayoutEffect(() => {
    if (referenceRef.current && popoverRef.current) {
      popper.current = createPopper(referenceRef.current, popoverRef.current, {
        placement: originalPlacement,
        strategy: fixed ? 'fixed' : 'absolute',
        modifiers: [
          {
            name: 'eventListeners',
            enabled: eventsEnabled
          },
          {
            name: 'applyStyles',
            enabled: false
          },
          {
            name: 'flip',
            enabled: flip,
            options: { padding: 8 }
          },
          {
            name: 'computeStyles',
            options: { gpuAcceleration: false, adaptive: false }
          },
          {
            name: 'offset',
            options: { offset }
          },
          {
            name: 'preventOverflow',
            enabled: preventOverflow
          },
          {
            name: 'updateState',
            phase: 'write',
            enabled: true,
            fn({ state }) {
              setPlacement(state.placement);
              setPopoverStyles(state.styles.popper as React.CSSProperties);
            }
          },
          ...modifiers
        ]
      });
    }
    return () => {
      if (popper.current) {
        popper.current.destroy();
        popper.current = null;
      }
    };
  }, [originalPlacement, fixed, forceUpdate, flip, offset, preventOverflow, eventsEnabled]);

  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => {
      if (forceUpdate) {
        if (popper.current) {
          popper.current.forceUpdate();
        }
      }
    });
    return () => {
      cancelAnimationFrame(id);
    };
  }, [forceUpdate]);

  return {
    popperInstance: popper.current,
    reference: {
      ref: referenceRef
    },
    popper: {
      ref: popoverRef,
      style: popoverStyles
    },
    update,
    placement,
    place
  };
}

export type UsePopperReturn = ReturnType<typeof usePopper>;
